<template>
  <div class="custom_select" ref="dropdownRef">
    <div class="select_box" :class="{ select_box_active: isOpen }" @click="toggleDropdown">
      <span class="label_box" :class="{ label_box_not_empty: selectedLabel }">
        <span class="label" :class="{ label_gray: !(route.name !== 'Project' && selectedLabel) }">{{
          route.name !== 'Project' && selectedLabel ? selectedLabel : placeholder
        }}</span>
      </span>
      <span class="iconfont iconfont_down icon-xiangxiajiantou"></span>
    </div>

    <div v-if="isOpen" class="dropdown">
      <div v-if="searchable" class="search_box">
        <FsInput
          v-model="searchText"
          new-regular
          type="text"
          :search="true"
          placeholder="Search"
          class="search-input"
        />
      </div>

      <ul class="options_list" v-if="filteredOptions.length">
        <li
          v-for="option in filteredOptions"
          :key="option.value"
          @click="selectOption(option)"
          class="option_item"
          :class="{ option_item_active: selectedLabel && props.modelValue === option.value }"
        >
          {{ option.label }}
        </li>
      </ul>
      <div class="no_result" v-if="!filteredOptions.length">
        {{ searchEmptyText }}
      </div>
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { FsInput } from 'fs-design'
import { trackEvent } from '@/utils/analytics'

interface Option {
  label: string
  value: string | number
  templateId?: number
  isCustomized?: number
}

interface Props {
  modelValue: string | number | null
  options: Option[]
  placeholder?: string
  searchPlaceholder?: string
  searchable?: boolean
  searchEmptyText?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Please Select',
  searchPlaceholder: 'Search',
  searchable: false,
  searchEmptyText: 'no result',
})

const emit = defineEmits(['update:modelValue', 'change'])

const route = useRoute()
const isOpen = ref(false)
const searchText = ref('')
const dropdownRef = ref<HTMLElement | null>(null)

const toggleDropdown = () => {
  trackEvent({
    eventCategory: 'dec_topology',
    eventAction: 'select',
    eventLabel: 'select_project',
  })
  isOpen.value = !isOpen.value
}

const filteredOptions = computed(() => {
  if (!props.searchable || !searchText.value) return props.options
  return props.options.filter(option => option.label.toLowerCase().includes(searchText.value.toLowerCase()))
})

const selectedLabel = computed(() => {
  return props.options.find(option => option.value === props.modelValue)?.label ?? ''
})

const selectOption = (option: Option) => {
  emit('update:modelValue', option.value)
  emit('change', option)
  trackEvent({
    eventCategory: 'dec_topology',
    eventAction: 'project_select',
    eventLabel: `${option.value}-${option.label}`,
  })
  isOpen.value = false
  searchText.value = ''
}

const hideDropdown = () => {
  isOpen.value = false
}
const handleClickOutside = e => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    hideDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('closeAllDropdowns', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.addEventListener('closeAllDropdowns', handleClickOutside)
})

defineExpose({
  hideDropdown,
})
</script>

<style scoped lang="scss">
.custom_select {
  position: relative;
  width: 300px;
}

.select_box {
  border: 1px solid #f6f6f8;
  border-radius: 4px;
  padding: 10px 12px;
  background-color: #f6f6f8;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  @include font13;
  transition: all 0.3s;
  overflow: hidden;
  position: relative;

  &:hover {
    background: #f6f6f8;

    &:after {
      display: block;
      content: ' ';
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      position: absolute;
      background-color: #19191a;
      opacity: 0.04;
      transition: all 0.3s;
      z-index: 1;
    }
  }

  &.select_box_active {
    border: 1px solid #707070;
    background: linear-gradient(0deg, rgba(25, 25, 26, 0.04), rgba(25, 25, 26, 0.04)), #f6f6f8;

    .iconfont_down {
      transform: rotate(-180deg);
    }

    &:after {
      display: block;
      content: ' ';
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      position: absolute;
      background-color: #19191a;
      opacity: 0.04;
      transition: all 0.3s;
      z-index: 1;
    }
  }

  .label_box {
    display: flex;
    align-items: center;
    flex: 1 1 auto;

    color: #707070;
    overflow: hidden;

    &.label_box_not_empty {
      color: #19191a;
    }
  }

  .label {
    flex: 1 1 auto;
    @include textOverflow;
    &.label_gray {
      color: #707070;
    }
  }

  .iconfont_add {
    display: inline-block;
    width: 16px;
    height: 16px;
    font-size: 16px;
    margin-right: 8px;
    line-height: 1;
    flex-shrink: 0;
  }

  .iconfont_down {
    flex-shrink: 0;
    display: inline-block;
    width: 12px;
    height: 12px;
    line-height: 1;
    font-size: 12px;
    color: #707070;
    transform: all 0.3s;
  }
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 10px;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  background: white;
  box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.06), 0px 2px 20px 0px rgba(0, 0, 0, 0.06);
  z-index: 1000;
}

.search_box {
  padding: 8px;
}

.no_result {
  padding: 24px 16px;
  text-align: center;
  @include font14;
  color: #707070;
}

.options_list {
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
  padding: 0 8px 8px 8px;
}

.option_item {
  @include font13;
  color: #707070;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s;
  padding: 8px 12px;
  height: 36px;
  width: 100%;
  @include textOverflow;
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    background: rgba(25, 25, 26, 0.04);
  }
  &.option_item_active {
    background: rgba(25, 25, 26, 0.06);
    color: #19191a;
  }
}
</style>
